import { Button, FAIcon } from '@/components'
import { Module, ModuleOptionsValue } from '@/modules/companies/consts'
import { SalaryType } from '@/modules/companies/types.graphql'
import { useConfirm, useModuleConfig } from '@/utils/hooks'
import { useMutation, useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { BetaChip } from '../generation-preview/beta-chip'
import { GenerationRuleDialog } from './generation-rule-dialog'
import { freeRuleKinds } from './generation-rule-kinds'
import { SalaryWorkTimeHeading } from './salary-work-time-heading'
import type {
  GenerationRuleCalculation,
  GenerationRuleKind,
  GenerationRuleRestHandling,
  GenerationRuleRounding,
} from './generation-rule-labels'
import {
  CHAIN_CALCULATIONS,
  useGenerationRuleLabels,
} from './generation-rule-labels'
import { DELETE_SALARY_ITEM_GENERATION_RULE_MUTATION } from './mutations'
import type { DietSettingsSalaryWorkTime, GenerationRuleNode } from './queries'
import { DIET_SETTINGS_SALARY_WORK_TIMES_QUERY } from './queries'

export const GenerationRulesSection: React.FC = () => {
  const translations = useTranslate({
    title: 'settings.generation-rules.title',
    description: 'settings.generation-rules.description',
    disabledHint: 'settings.generation-rules.disabled-hint',
    noSalarySettings: 'settings.generation-rules.no-salary-settings',
  })
  const { getModuleOptionStatus } = useModuleConfig(Module.SALARY)
  const enabled = getModuleOptionStatus(
    ModuleOptionsValue.SALARY_GENERATION_RULES
  )
  const { data } = useQuery(DIET_SETTINGS_SALARY_WORK_TIMES_QUERY)
  const salaryWorkTimes = data?.allSalaryWorkTimes ?? []

  return (
    <section className="overflow-hidden rounded-2xl bg-white shadow-card">
      <div className="space-y-2 px-5 py-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-stone-900">
            {translations.title}
          </h2>
          <BetaChip />
        </div>
        <p className="text-sm text-stone-600">{translations.description}</p>
        {!enabled && (
          <p className="text-sm text-stone-600">{translations.disabledHint}</p>
        )}
      </div>
      {data && salaryWorkTimes.length === 0 && (
        <p className="border-t border-stone-200 px-5 py-4 text-sm text-stone-600">
          {translations.noSalarySettings}
        </p>
      )}
      {salaryWorkTimes.map(salaryWorkTime => (
        <SalaryWorkTimeRules
          key={salaryWorkTime.id}
          salaryWorkTime={salaryWorkTime}
        />
      ))}
    </section>
  )
}

const SalaryWorkTimeRules: React.FC<{
  salaryWorkTime: DietSettingsSalaryWorkTime
}> = ({ salaryWorkTime }) => {
  const translations = useTranslate({
    empty: 'settings.generation-rules.empty',
    deletePrompt: 'settings.generation-rules.delete-prompt',
    inactive: 'settings.generation-rules.inactive',
    delete: 'common.delete',
  })
  const confirm = useConfirm()
  const salaryDaysAllowed = salaryWorkTime.salaryType === SalaryType.DAY
  const rules = salaryWorkTime.generationRules
  const addKinds = freeRuleKinds(
    rules.map(rule => rule.kind),
    salaryDaysAllowed
  )

  const [deleteRule] = useMutation(
    DELETE_SALARY_ITEM_GENERATION_RULE_MUTATION,
    { refetchQueries: [{ query: DIET_SETTINGS_SALARY_WORK_TIMES_QUERY }] }
  )

  async function handleDelete(rule: GenerationRuleNode) {
    const { data: confirmed } = await confirm(
      translations.deletePrompt,
      undefined,
      { confirmText: translations.delete }
    )
    if (confirmed) await deleteRule({ variables: { id: rule.id } })
  }

  return (
    <div className="flex flex-col gap-3 border-t border-stone-200 px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SalaryWorkTimeHeading salaryWorkTime={salaryWorkTime} />
        {addKinds.length > 0 && (
          <GenerationRuleDialog
            salaryWorkTimeId={salaryWorkTime.id}
            kinds={addKinds}
          />
        )}
      </div>

      {rules.length === 0 ? (
        <p className="text-sm text-stone-600">{translations.empty}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rules.map(rule => (
            <li
              key={rule.id}
              className="flex items-center justify-between gap-4 rounded-lg border border-stone-200 p-3"
            >
              <GenerationRuleSummary rule={rule} />
              <div className="flex shrink-0 items-center gap-1">
                {!rule.isActive && (
                  <span className="text-xs text-stone-500">
                    {translations.inactive}
                  </span>
                )}
                <GenerationRuleDialog
                  salaryWorkTimeId={salaryWorkTime.id}
                  kinds={freeRuleKinds(
                    rules
                      .filter(other => other.id !== rule.id)
                      .map(other => other.kind),
                    salaryDaysAllowed
                  )}
                  rule={rule}
                />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => void handleDelete(rule)}
                >
                  <FAIcon icon="trash" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function GenerationRuleSummary({ rule }: { rule: GenerationRuleNode }) {
  const labels = useGenerationRuleLabels()
  const translations = useTranslate({
    perDuration: 'settings.generation-rules.per-duration-hours',
  })
  const calculation = rule.rule as GenerationRuleCalculation
  const codes = [rule.dietSalaryCode?.name, rule.salaryDaySalaryCode?.name]
    .filter(Boolean)
    .join(' · ')

  const details = CHAIN_CALCULATIONS.includes(calculation)
    ? labels.restHandling[rule.restHandling as GenerationRuleRestHandling]
    : [
        rule.amount,
        rule.perDurationHours
          ? `${translations.perDuration}: ${rule.perDurationHours} · ${
              labels.rounding[rule.rounding as GenerationRuleRounding]
            }`
          : null,
      ]
        .filter(value => value !== null)
        .join(' · ')

  return (
    <div className="flex flex-col">
      <span className="font-medium">
        {labels.kind[rule.kind as GenerationRuleKind]} ·{' '}
        {labels.rule[calculation]}
      </span>
      <span className="text-secondary text-sm">
        {[codes, details].filter(Boolean).join(' — ')}
      </span>
    </div>
  )
}
